import { useEffect, useRef } from 'react';
import { options } from '../optionsStorage';
import { createProvider } from '../aiProviders';
import { AICharacter } from '../aiCharacter';

let globalAICharacter = null;

export const initializeAI = async () => {
  if (!options.aiEnabled || !window.bot) return null;

  if (!globalAICharacter) {
    try {
      // Use the PLAYER's own bot, not a second connection
      globalAICharacter = new AICharacter({ mode: options.aiMode });
      await globalAICharacter.initialize(window.bot);
      console.log('[AI] AI initialized with player bot');
    } catch (err) {
      console.error('[AI] Failed to initialize:', err);
      globalAICharacter = null;
      return null;
    }
  }

  return { aiCharacter: globalAICharacter };
};

export const shutdownAI = () => {
  if (globalAICharacter) {
    try {
      globalAICharacter.cancel();
    } catch { }
  }
  globalAICharacter = null;
};

function buildProviderChain() {
  const chain = [];
  const primary = options.aiProvider;

  const configFor = (name) => {
    const responseLength = options.aiResponseLength;
    if (name === 'gemini') return { apiKey: options.aiGeminiApiKey, responseLength };
    if (name === 'openai') return { apiKey: options.aiOpenAIApiKey, model: 'gpt-4o-mini', responseLength };
    if (name === 'openrouter') return { apiKey: options.aiOpenRouterApiKey, model: options.aiOpenRouterModel, responseLength };
    return { responseLength };
  };

  const hasCredentials = (name) => {
    if (name === 'pollinations') return true;
    const cfg = configFor(name);
    return !!cfg.apiKey;
  };

  if (primary !== 'pollinations' && hasCredentials(primary)) {
    chain.push(createProvider(primary, configFor(primary)));
  }
  chain.push(createProvider('pollinations', configFor('pollinations')));
  return chain;
}

export const processAIMessage = async (userMessage) => {
  if (!options.aiEnabled) return null;

  try {
    const ai = await initializeAI();
    if (!ai) {
      window.bot?.chat('[AI] AI not available');
      return null;
    }
    const { aiCharacter } = ai;

    const state = aiCharacter.getState();
    const context = {
      aiPos: state.pos,
      aiMode: state.mode,
      aiGameMode: state.gameMode,
      playerPos: state.pos,
      aiHealth: state.health,
      aiFood: state.food,
      inventory: state.inventory || {},
      nearbyBlocks: getNearbyBlocks(window.bot, 8),
    };

    const providerChain = buildProviderChain();
    let response = null;
    let lastError = null;

    for (const provider of providerChain) {
      try {
        response = await Promise.race([
          provider.query(context, userMessage),
          new Promise((_, reject) => setTimeout(() => reject(new Error('Provider timeout')), 20_000))
        ]);
        break;
      } catch (err) {
        lastError = err;
        console.warn(`[AI] Provider failed:`, err.message);
      }
    }

    if (!response || !response.actions) {
      window.bot?.chat(`[AI] Failed to get response`);
      return null;
    }

    if (response.thought) console.log('[AI Thought]', response.thought);

    const actions = response.actions.slice(0, 50);
    for (const action of actions) {
      aiCharacter.queueAction(action);
    }
    await aiCharacter.runQueue();

    return response;
  } catch (err) {
    console.error('[AI] Error:', err);
    window.bot?.chat(`[AI] Error: ${err.message}`);
    return null;
  }
};

function getNearbyBlocks(bot, radius = 8) {
  if (!bot?.world || !bot.entity) return {};
  const blocks = {};
  const pos = bot.entity.position;
  const cx = Math.floor(pos.x);
  const cy = Math.floor(pos.y);
  const cz = Math.floor(pos.z);
  for (let x = cx - radius; x <= cx + radius; x++) {
    for (let z = cz - radius; z <= cz + radius; z++) {
      for (let y = cy - 3; y <= cy + 3; y++) {
        const block = bot.world.getBlock({ x, y, z });
        if (block && block.name !== 'air') {
          blocks[block.name] = (blocks[block.name] || 0) + 1;
        }
      }
    }
  }
  return blocks;
}

export function useAIManager() {
  const initRef = useRef(false);

  useEffect(() => {
    if (options.aiEnabled && !initRef.current) {
      initRef.current = true;
      initializeAI().catch(console.error);
    } else if (!options.aiEnabled && initRef.current) {
      initRef.current = false;
      shutdownAI();
    }
  }, [options.aiEnabled]);

  return {
    aiEnabled: options.aiEnabled,
    aiMode: options.aiMode,
    aiProvider: options.aiProvider,
  };
}

export default processAIMessage;
